<template>
  <b-modal
    :visible="showModal"
    id="upload-jd-modal"
    centered
    hide-footer
    hide-header
    no-close-on-backdrop
    :no-close-on-esc="showLoader"
    @hidden="closeModal"
  >
    <div class="cs-modal-header">
      <div>Upload JD</div>
      <div
        :class="showLoader ? 'upload-jd-disabled' : 'pointer'"
        @click="closeModal"
      >
        <img
          :class="{ 'rotate-on-hover': !showLoader }"
          src="../../../assets/img/accessconnew/blueCross.svg"
          alt=""
        />
      </div>
    </div>
    <div class="upload-jd-body">
      <div class="upload-jd-subtxt">
        Upload a job description and we will fill in the job details for you to
        review.
      </div>
      <div v-if="!jdFile" class="upload-jd-dropzone mt-3">
        <b-form-file
          v-model="jdFile"
          @input="handleFile"
          accept=".pdf, .doc, .docx"
        ></b-form-file>
        <img src="../../../assets/talentpool/addPage.svg" alt="" />
        <div class="upload-jd-droptitle mt-2">Drag & drop your JD here</div>
        <div class="upload-jd-dropsub mt-1">
          or <span class="upload-jd-browse">browse</span> from your computer
        </div>
      </div>
      <div
        v-else
        class="upload-jd-filecard d-flex align-items-center mt-3"
        :class="{ 'upload-jd-filecard-error': errorFile }"
      >
        <img src="../../../assets/img/document.svg" alt="" />
        <div class="flex-grow-1 text-truncate mx-3">
          <div class="upload-jd-filename text-truncate">{{ jdFile.name }}</div>
          <div class="upload-jd-filemeta" :class="{ 'text-danger': errorFile }">
            {{
              errorFile
                ? "Unsupported format. Please upload a PDF, DOC or DOCX file."
                : fileSize
            }}
          </div>
        </div>
        <img
          v-if="!showLoader"
          class="pointer"
          src="../../../assets/talentpool/Deletefile.svg"
          alt=""
          @click="removeFile"
        />
      </div>
      <div class="upload-jd-hint mt-2">
        {{
          showLoader
            ? "Reading your JD, this can take a few seconds..."
            : "Supported formats: PDF, DOC, DOCX"
        }}
      </div>
    </div>
    <div class="upload-jd-footer d-flex justify-content-end align-items-center">
      <b-button
        class="acs-cancelBtn-modal upload-jd-back w-95px"
        :disabled="showLoader"
        @click="closeModal"
        >Back</b-button
      >
      <div class="position-relative ml-3">
        <b-button
          :disabled="!jdFile || errorFile"
          class="acs-addBtn-modal w-95px"
          @click="uploadJD"
          :class="showLoader ? 'upload-loader-class defaultCursor' : 'pointer'"
          >{{ showLoader ? "" : "Upload" }}</b-button
        >
        <div v-if="showLoader" class="upload-loader-pos">
          <Loader />
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import toast from "../../../components/mixin/toast";
import Loader from "../../../components/fixedcomponents/Loader.vue";
import { getRequiredDocuments, jdParser } from "../../../utils/apiFunctions";
import { getCustomFields } from "../../../utils/customFields";

export default {
  name: "UploadJDModal",
  mixins: [toast],
  components: { Loader },
  props: {
    showModal: Boolean,
  },
  data() {
    return {
      jdFile: null,
      errorFile: false,
      showLoader: false,
    };
  },
  computed: {
    fileSize() {
      const size = this.jdFile ? this.jdFile.size : 0;
      return size >= 1024 * 1024
        ? `${(size / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.max(1, Math.round(size / 1024))} KB`;
    },
  },
  methods: {
    handleFile(file) {
      if (!file) return;
      const extension = file.name.split(".").pop().toLowerCase();
      this.errorFile = !["pdf", "doc", "docx"].includes(extension);
    },
    removeFile() {
      this.jdFile = null;
      this.errorFile = false;
    },
    closeModal() {
      if (this.showLoader) return;
      this.removeFile();
      this.$emit("close-modal");
    },
    async uploadJD() {
      if (!this.jdFile || this.errorFile || this.showLoader) return;
      this.showLoader = true;
      try {
        // Same flow as the candidate resume upload: form config from the
        // recruitment backend first, then that config goes to the parser.
        const [jdFields] = await getCustomFields("1", "add");
        if (!jdFields) {
          this.showReadError();
          return;
        }
        await this.addDocumentOptions(jdFields);
        const formData = new FormData();
        formData.append("file", this.jdFile);
        formData.append("jd_form", JSON.stringify(jdFields));
        const res = await jdParser(formData);
        const payload = res.data.payload || {};
        const hasDetails = Object.values(payload).some((value) =>
          Array.isArray(value) ? value.length : value
        );
        if (res.status == 200 && res.data.state == "success" && hasDetails) {
          this.$emit("parsed", payload);
        } else if (res.status == 200 && res.data.state == "success") {
          this.dynamicToast(
            "warning",
            "No job details found",
            `We couldn't find any job details in "${this.jdFile.name}". Please upload a different file or create the job manually.`
          );
        } else {
          this.showReadError();
        }
      } catch (error) {
        console.error(error);
        this.showReadError();
      } finally {
        this.showLoader = false;
      }
    },
    // "Choose Documents" gets its options from the license's document list, not
    // from the form config, so the parser would have nothing to match. Give it
    // that list; if it can't be loaded, the field is simply left for the user.
    async addDocumentOptions(jdFields) {
      const field = jdFields.find((f) => f.key === "requiredDocumets");
      if (!field || (field.values && field.values.length)) return;
      try {
        const res = await getRequiredDocuments();
        field.values = res.data.data || [];
      } catch (error) {
        console.error(error);
      }
    },
    showReadError() {
      this.dynamicToast(
        "warning",
        "Couldn't read the JD",
        `Something went wrong while reading "${this.jdFile.name}". Please try again or create the job manually.`
      );
    },
  },
};
</script>

<style scoped>
.upload-jd-body {
  padding: 20px 25px 0;
}
.upload-jd-subtxt {
  font-family: "Lato", sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #3a3c40;
}
.upload-jd-dropzone {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 170px;
  border: 1.5px dashed #20639b;
  border-radius: 12px;
  background: #f8fbff;
  transition: background 0.2s;
}
.upload-jd-dropzone:hover {
  background: #f1f7ff;
}
.upload-jd-droptitle {
  font-family: "Lato", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: #000000;
}
.upload-jd-dropsub {
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #3a3c40;
}
.upload-jd-browse {
  color: #20639b;
  font-weight: 600;
  text-decoration: underline;
}
.upload-jd-filecard {
  padding: 14px 16px;
  border: 1px solid #d0d5dd;
  border-radius: 12px;
  background: #ffffff;
}
.upload-jd-filecard-error {
  border-color: #e53935;
}
.upload-jd-filename {
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #333333;
}
.upload-jd-filemeta {
  font-family: "Lato", sans-serif;
  font-size: 12px;
  color: #8e8e8e;
}
.upload-jd-hint {
  font-family: "Lato", sans-serif;
  font-size: 12px;
  color: #8e8e8e;
}
.upload-jd-footer {
  padding: 24px 25px 20px;
}
.upload-jd-disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.upload-jd-back:disabled {
  color: #9e9e9e !important;
  border-color: #d0d5dd !important;
  background-color: #f5f5f5 !important;
  opacity: 1;
  cursor: not-allowed;
}
.upload-loader-class {
  opacity: 0.5;
  height: 29px;
  width: 95px;
}
.upload-loader-pos {
  position: absolute;
  top: -54px;
  z-index: 1;
  left: 0px;
}
</style>
<style>
.upload-jd-dropzone .custom-file {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
}
.upload-jd-dropzone .custom-file-input,
.upload-jd-dropzone .custom-file-label {
  height: 100%;
  cursor: pointer;
}
</style>
